import { useState, useEffect, useMemo } from 'react';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import { useReduxTemplateFiles } from '@Hooks/reduxTemplateFilesHook/useReduxTemplateFiles';
import { X, Reply as ReplyIcon, Phone, Link2 } from 'lucide-react';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsappTemplatePreview.scss';

export default function WhatsappTemplatePreview(props) {

    const { currentLeadsId, setTemplateList, templateList } = useReduxLeads();
    const { templateFiles } = useReduxTemplateFiles();
    const [template, setTemplate] = useState(null);
    const [fileUrl, setFileUrl] = useState('');
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const errorHandler = new ErrorHandler();

    useEffect(()=> {
        renderData();
    },[props])

    const renderData = async ()=> {
        try {
            if(!!props.id) {
                let templateData = templateList?.find((item)=> item.id === props.id);
                if(!!templateData) {
                    templateData = {...templateData, variables: extractVariables(getBody(templateData.components))};
                    setTemplate(templateData);
                    let whatsappFile = templateFiles[templateData?.name];
                    if(!!whatsappFile) {
                        setFileUrl(whatsappFile?.publicUrl);
                    }
                }
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const extractVariables = (content) => {
        const matches = content.match(/{{(\w+)}}/g) || [];
        return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
    };

    // Helper to get header (text or media)
    const getHeader = (components) => {
        if(!!components) {
            const header = components.find(c => c.type === 'HEADER');
            if (!header) return <div/>;
            if (header.format === 'TEXT') {
                return <div className="whatsapp-header-text">{header.text}</div>;
            }
            if (header.format === 'VIDEO' && header.example && header.example.header_handle && header.example.header_handle[0]) {
                return (
                    <div className="whatsapp-header-media">
                        <video controls width="220" style={{borderRadius:8}}>
                            <source src={fileUrl !== '' ? fileUrl : header.example.header_handle[0]} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                );
            }
            if (header.format === 'IMAGE' && header.example && header.example.header_handle && header.example.header_handle[0]) {
                return (
                    <div className="whatsapp-header-media">
                        <img alt='Header Image' src={fileUrl !== '' ? fileUrl : header.example.header_handle[0]} />
                    </div>
                );
            }
        }
        return <div/>;
    };

    // Helper to get body text (with positional variables)
    const getBody = (components, example) => {
        if(!!components) {
            const body = components.find(c => c.type === 'BODY');
            if (!body) return '';
            let text = body.text;
            // Replace {{1}}, {{2}}, ... with example values if available
            if (body.example && body.example.body_text && body.example.body_text[0]) {
                body.example.body_text[0].forEach((val, idx) => {
                    text = text.replace(new RegExp(`{{${idx+1}}}`, 'g'), val);
                });
            }
            return text;
        }
    };

    const getButtons = (components = []) => {
        const btns = components.find(c => c.type === "BUTTONS");
        if (!btns?.buttons?.length) return <div/>;
        return (
            <div className="whatsappSenderTemplatePreview-buttons">
                {btns.buttons.map((b, i) => (
                    b.type === 'URL' ?
                        <a href={b.url} target='_blank' key={i}>
                            <button
                                className="whatsappSenderTemplatePreview-button">
                                    <Link2 size={16}/>
                                    {b.text}
                             </button>
                         </a>
                    :
                        <button
                            key={i}
                            className="whatsappSenderTemplatePreview-button">
                                {b.type === 'QUICK_REPLY' && <ReplyIcon size={16}/>}
                                {b.type === 'PHONE_NUMBER' && <Phone size={14}/>}
                                {b.text}
                        </button>
                ))}
            </div>
        );
    };

    const getFooter = (components = []) => {
        const footer = components.find(c => c.type === "FOOTER");
        if (!footer?.text) return null;

        return (
            <div className="whatsappSenderTemplatePreview-footer">
                {footer.text}
            </div>
        );
    };

    return (
        !props.hideBackground ?
            <div className="template-preview">
                <div className='flex-row-center-space-between'>
                    <h3>Template Preview</h3>
                    {!!props.onClose &&
                        <div onClick={props.onClose} className={'template-preview-close'}>
                        <X />
                        </div>
                    }
                </div>
                <div className="whatsappSenderTemplatePreview-container">
                    <div className="whatsappSenderTemplatePreview-card">
                        <div className="whatsappSenderTemplatePreview-header">
                            {getHeader(template?.components)}
                        </div>
                        <div className="whatsappSenderTemplatePreview-body">
                            <WhatsappText text={getBody(template?.components)} />
                        </div>

                        {getFooter(template?.components)}
                        {getButtons(template?.components)}
                    </div>
                </div>
            </div>
        :
            <div className="whatsappSenderTemplatePreview-container">
                <div className="whatsappSenderTemplatePreview-card">
                    <div className="whatsappSenderTemplatePreview-header">
                        {getHeader(template?.components)}
                    </div>
                    <div className="whatsappSenderTemplatePreview-body">
                        <WhatsappText text={getBody(template?.components)} showReadMore={props.showReadMore} />
                    </div>

                    {getFooter(template?.components)}
                    {getButtons(template?.components)}
                </div>
            </div>
    );
}

const WhatsappText = ({ text = "", showReadMore = true }) => {
    const WORD_LIMIT = showReadMore ? 20 : 100000;
    const [expanded, setExpanded] = useState(false);
    const words = useMemo(
        () => text.trim().split(/\s+/).filter(Boolean),
        [text]
    );
    const shouldTruncate = words.length > WORD_LIMIT;
    const displayText = useMemo(() => {
        if (expanded || !shouldTruncate) {
            return text;
        }
        let wordCount = 0;
        const truncated = text
            .split(/(\s+)/)
            .filter((part) => {
                if (/\s+/.test(part)) {
                    return true;
                }
                wordCount++;
                return wordCount <= WORD_LIMIT;
            })
            .join("")
            .trimEnd();
        return `${truncated}...`;
    }, [text, expanded, shouldTruncate]);

    const TOKEN_REGEX = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`|\n)/g;
    const tokens = displayText.split(TOKEN_REGEX).filter(Boolean);

    return (
        <span className="whatsapp-text">
            {tokens.map((token, i) => {
                if (token === "\n") {
                    return <br key={i} />;
                }
                if (token.startsWith("*") && token.endsWith("*")) {
                    return <strong key={i}>{token.slice(1, -1)}</strong>;
                }
                if (token.startsWith("_") && token.endsWith("_")) {
                    return <em key={i}>{token.slice(1, -1)}</em>;
                }
                if (token.startsWith("~") && token.endsWith("~")) {
                    return (
                        <span
                            key={i}
                            className="whatsapp-text__strike"
                        >
                            {token.slice(1, -1)}
                        </span>
                    );
                }
                if (token.startsWith("`") && token.endsWith("`")) {
                    return (
                        <code key={i} className="whatsapp-text__code">
                            {token.slice(1, -1)}
                        </code>
                    );
                }
                return <span key={i}>{token}</span>;
            })}
            {shouldTruncate && (
                <button
                    type="button"
                    className="whatsapp-text__toggle"
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded ? "Read less" : "Read more"}
                </button>
            )}
        </span>
    );
};
